/**
 * This file is responsible for rendering the category page in the application.
 * It fetches and displays a specific category and its associated items.
 *
 * The component uses server-side rendering (getServerSideProps) to fetch the category and its items.
 * If the category does not exist or there are no items in the category, a not found page is returned.
 *
 * The component also handles redirects based on the user's language and locality settings.
 * If a redirect is necessary, the user is redirected to the appropriate category page.
 *
 * The component uses the 'next/router' library to access the locale from the router.
 * This locale is used to fetch the corresponding category and items for the user's language.
 *
 * The component also handles loading states and errors during the fetching of the category and items.
 * If there's an error during the fetch, it is stored in the state and displayed to the user.
 *
 * The Category and CategoryHeader components from '@Hotels-at-Home/hah-fe-core' are used to render the category and its header.
 */

/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, ReactElement } from 'react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getCategoryLink, getCategory } from '@Hotels-at-Home/hah-fe-core/dist/utils/category';
import Category from '@Hotels-at-Home/hah-fe-core/dist/components/Category';
import CategoryHeader from '../../ui/pages/Category/CategoryHeader';
import { getCookie } from '@Hotels-at-Home/hah-fe-core/dist/utils';
import { dataLayerCategory } from '@Hotels-at-Home/hah-fe-core/dist/utils/dataLayer';
import { publicQuery, UrlResolver } from '@Hotels-at-Home/hah-fe-core/dist/services/graphql';

import { NextPage } from '@Hotels-at-Home/hah-fe-core/dist/components';
import Head from 'next/head';
import { productNameFilters, siteName, parentHotelCompany } from '~/constants/siteConfig';
import apolloConfig from '~/constants/apolloConfig';

import {
  TServerSideProps,
  IBanner,
  IScandiCategoryChildItem,
  ICategory,
  IUrlResolverRequest,
  IUrlResolverResponse,
} from '@Hotels-at-Home/hah-fe-core/dist/types';

import { LanguageCode, COOKIE_LOCALITY_NAME } from '@Hotels-at-Home/hah-fe-core/dist/constants';

import {
  getHomepageRedirect,
  getRedirectCategory,
  shouldRedirect,
} from '@Hotels-at-Home/hah-fe-core/dist/utils/redirect';
import { wrapper } from '~/store';
import {
  filterExternalHrefData,
  generateEuHrefData,
  getHrefData,
  homepageHrefLangTags,
} from '~/utils/hreflangs';
import { useTranslation } from 'next-i18next';
import languageParser from 'accept-language-parser';
import { updateCategory } from '@Hotels-at-Home/hah-fe-core/dist/store/category';
import { getStructuredData } from '@Hotels-at-Home/hah-fe-core/dist/utils/schema';
import CategorySEO from '~/ui/pages/Category/CategorySEO';

interface ICategoryPage {
  banner: IBanner;
  category: ICategory;
  categoryItems: IScandiCategoryChildItem[];
  isScandiCategoryItemsUsed: boolean;
}

interface HrefDataCategory {
  [key: string]: any;
}

type TProps = TServerSideProps<ICategoryPage> & {
  languageUrls?: {
    href: string;
    value: string;
  }[];
};

const CategoryPage = (props: TProps): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '' });
  const { error, category, categoryItems, banner, languageUrls } = props;

  const router = useRouter();
  const locale = router.locale as LanguageCode;
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;

  const hrefDataCategory = getHrefData(category, locale);
  const externalHrefData = filterExternalHrefData(hrefDataCategory);
  const euHrefData = generateEuHrefData(languageUrls, siteUrl, '/c/');

  const meta = {
    title: category?.meta_title || `${t('$local.$meta.meta_title')} - ${category?.name}`,
    description: category?.meta_description || category?.description,
    keyword: category?.meta_keywords,
    url: `${siteUrl}/${locale}${router.asPath}`,
    image: category?.image || category?.seo_copy_image,
  };

  const organizationSchema = getStructuredData();
  organizationSchema.url = siteUrl;

  useEffect(() => {
    if (!category || !categoryItems.length) return;
    dataLayerCategory({
      affiliation: parentHotelCompany,
      brand: siteName,
      category,
      categoryItems,
      locale,
      siteFilters: productNameFilters,
    });
  }, [categoryItems, category]);

  const canonicalUrl = `${siteUrl}/${locale}${new URL(router.asPath, siteUrl).pathname}`;

  return (
    <NextPage error={error} meta={meta} siteUrl={siteUrl}>
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        {euHrefData.map(
          ({ href, url, rel }) =>
            href && <link key={`${href}`} rel={rel} hrefLang={href} href={url} />,
        )}
        {externalHrefData.map(
          ({ href, hreflang, rel }) =>
            href && <link key={`${hreflang} ${rel}`} rel={rel} hrefLang={hreflang} href={href} />,
        )}
      </Head>
      {category && (
        <Category
          category={category}
          categoryItems={categoryItems}
          banner={banner}
          categoryHeaderOverride={<CategoryHeader category={category} />}
          enableInlineCategoryHeader
          categorySEOOverride={<CategorySEO category={category} />}
        />
      )}
    </NextPage>
  );
};

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps(
  store => async ctx => {
    const slug = ctx.params.slug as string[];
    const localityCookie = getCookie(COOKIE_LOCALITY_NAME, ctx);
    const userLanguage = ctx.req.headers['accept-language'] as string;
    const path = ctx.query.slug as string[];
    const url = path.join('/');

    const {
      banner,
      category,
      categoryItems,
      isScandiCategoryItemsUsed,
      redirectUrl,
      notFound,
      error,
    } = await getCategory(apolloConfig, slug.join('/'), ctx);

    store.dispatch(updateCategory(category));

    if (redirectUrl) {
      return {
        redirect: {
          destination: getCategoryLink(redirectUrl, ctx.locale),
          permanent: false,
        },
      };
    }

    if (notFound) {
      return { notFound: true };
    }

    const props: TProps = {
      ...(await serverSideTranslations(ctx.locale)),
      error,
      banner,
      category,
      categoryItems,
      isScandiCategoryItemsUsed,
    };

    const resolvedCategory = await publicQuery<IUrlResolverResponse, IUrlResolverRequest>(
      apolloConfig(ctx.locale as LanguageCode),
      {
        query: UrlResolver,
        variables: {
          url,
        },
      },
      'UrlResolver',
      ctx,
    ).then(res => res.data.urlResolver);

    // if the resolvedCategory.language_urls object contains a key "__typename", remove it from the object
    if (Object.prototype.hasOwnProperty.call(resolvedCategory.language_urls, '__typename')) {
      // eslint-disable-next-line no-underscore-dangle
      delete (resolvedCategory.language_urls as any).__typename;
    }

    const resolvedCategoryArray = Object.entries(resolvedCategory.language_urls);

    props.languageUrls = resolvedCategoryArray.map(([key, value]) => ({
      href: key.replace('url_', ''),
      value,
    }));

    if (shouldRedirect(ctx)) {
      const parsedLanguage = languageParser.parse(userLanguage)[0];
      const hrefDataCategory: HrefDataCategory = getHrefData(category, ctx.locale, true);
      const localityParam = ctx.query.locality; // can be used to override the locality header for testing
      const localityHeader =
        (ctx.req.headers.Locality as string) || (ctx.req.headers.locality as string);
      const desiredLocale = localityParam ? localityParam.toString().toUpperCase() : localityHeader;

      // TODO: Refactor to include in the same flow as en-us
      if (parsedLanguage?.code === 'zh') {
        const newRedirect = `${parsedLanguage.code}-cn`;
        const hrefData = hrefDataCategory.find(
          (item: { hreflang: string }) => item.hreflang === newRedirect,
        );
        if (typeof hrefData?.href === 'string') {
          return {
            redirect: {
              destination: hrefData.href,
              permanent: false,
            },
          };
        }
      }
      const hrefRedirect = getRedirectCategory(
        (localityCookie as string) || (desiredLocale as string),
      );
      const categoryRedirectUrl = hrefDataCategory.find(
        (item: { hreflang: string }) => item.hreflang === hrefRedirect,
      );

      // Only redirect if going outside EU; Internal redirects are handled in the middleware
      if (
        hrefRedirect !== 'en-gb' &&
        hrefRedirect !== 'fr' &&
        hrefRedirect !== 'de' &&
        hrefRedirect !== 'es'
      ) {
        if (categoryRedirectUrl?.href) {
          return {
            redirect: {
              destination: categoryRedirectUrl.href,
              permanent: false,
            },
          };
        }
        // no Referral and non EU IP
        // HREF data null > redirect

        const hpRedirect = getHomepageRedirect(localityCookie as string);
        const hpHrefData = homepageHrefLangTags.find(item => item.hrefLang === hpRedirect);
        return {
          redirect: {
            destination: hpHrefData.href,
            permanent: false,
          },
        };
      }
    }

    return { props };
  },
);

export default CategoryPage;
